import { HStack, Flex, Divider, VStack } from '@chakra-ui/layout';
import { Skeleton, Tooltip } from '@chakra-ui/react';
import React from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import { ResolvedLoanFragmentGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import { percentageToPercentageLabel } from '~/packages/common-frontend/utils';

const BenchmarkRateTooltipContent = ({
  loan,
}: {
  loan: ResolvedLoanFragmentGQLType;
}) => {
  const getPercentageLabel = (value: unknown) =>
    typeof value === 'number'
      ? percentageToPercentageLabel(value * 100, 5, true)
      : '-';

  return (
    <VStack gap="0.5rem" padding="1rem">
      <HStack justify="space-between" gap="6rem" w="100%">
        <span>Benchmark Rate</span>
        <span>
          {getPercentageLabel(loan?.pricing.current.baseRate.publishedRate)}
        </span>
      </HStack>
      <HStack justify="space-between" gap="6rem" w="100%">
        <span>Ceiling</span>
        <span>
          {getPercentageLabel(loan?.pricing.current.baseRate.ceiling)}
        </span>
      </HStack>
      <HStack justify="space-between" gap="6rem" w="100%">
        <span>Floor</span>
        <span>{getPercentageLabel(loan?.pricing.current.baseRate.floor)}</span>
      </HStack>
      <HStack justify="space-between" gap="6rem" w="100%">
        <span>Spread</span>
        <span>{getPercentageLabel(loan?.pricing.current.baseRate.spread)}</span>
      </HStack>
    </VStack>
  );
};

export const LoanPricingOption = ({
  loan,
  loading,
}: {
  loan: ResolvedLoanFragmentGQLType | undefined;
  loading: boolean;
}) => {
  const isFixedRate = loan?.pricing.current.baseRate.type
    .toLowerCase()
    .includes('fixed');
  // Not technically correct here - service fee is at the share level
  // But, this is fine for now
  const serviceFee =
    loan?.pricing.margins.find((margin) => margin.skimFee)?.skimFee?.rate ??
    null;
  return (
    <HStack
      sx={{
        w: '100%',
        gap: '1rem',
        wrap: 'wrap',
        alignItems: 'start',
        py: '.25rem',
      }}
    >
      <Flex fontWeight={500} w="20rem">
        Pricing Option
        {loan && ` (${loan.pricing.name})`}
      </Flex>
      <Flex
        sx={{
          border: '1px solid',
          borderColor: 'gray.200',
          borderRadius: 'md',
          flex: 1,
          flexDirection: 'column',
          gap: '.5rem',
          maxW: '100%',
          p: '1rem',
        }}
      >
        <HStack justifyContent="space-between">
          <Typography fontWeight="medium">All-in-Rate</Typography>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <Typography fontWeight="medium">
              {loan
                ? percentageToPercentageLabel(
                    loan.pricing.current.allInRate * 100,
                    5,
                    true,
                  )
                : '-'}
            </Typography>
          )}
        </HStack>
        <Divider />
        {!isFixedRate && (
          <>
            <HStack color={colors.gray[600]} justifyContent="space-between">
              <Typography variant="base-muted">Benchmark</Typography>
              {loading ? (
                <Skeleton w="10rem" h="1rem" />
              ) : (
                <Typography variant="base-muted">
                  {loan
                    ? commonUtils.toTitleCase(
                        loan.pricing.current.baseRate.name,
                      )
                    : '-'}
                </Typography>
              )}
            </HStack>
            <HStack justifyContent="space-between">
              <Flex maxW="50%">
                <Typography variant="base-muted">
                  Benchmark Determination Date
                </Typography>
              </Flex>

              {loading ? (
                <Skeleton w="10rem" h="1rem" />
              ) : (
                <Typography variant="base-muted">
                  {loan
                    ? commonDate.fullDateStrToHumanDateString(
                        loan.pricing.current.baseRate.determinationDate,
                      )
                    : '-'}
                </Typography>
              )}
            </HStack>
          </>
        )}
        <Tooltip
          label={loan && <BenchmarkRateTooltipContent loan={loan} />}
          placement="top-start"
          sx={{
            borderRadius: 'md',
            p: '0',
          }}
        >
          <HStack color={colors.gray[600]} justifyContent="space-between">
            <Typography variant="base-muted">
              {isFixedRate ? 'Fixed Rate' : 'Benchmark Rate'}
            </Typography>
            {loading ? (
              <Skeleton w="10rem" h="1rem" />
            ) : (
              <Typography variant="base-muted">
                {loan
                  ? percentageToPercentageLabel(
                      loan.pricing.current.baseRate.publishedRate * 100,
                      5,
                      true,
                    )
                  : '-'}
              </Typography>
            )}
          </HStack>
        </Tooltip>
        <HStack color={colors.gray[600]} justifyContent="space-between">
          <Typography variant="base-muted">WA Applicable Margin</Typography>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <Typography variant="base-muted">
              {loan
                ? percentageToPercentageLabel(
                    loan.pricing.current.weightedAverageMarginRate * 100,
                    5,
                    true,
                  )
                : 0}
            </Typography>
          )}
        </HStack>
        <HStack color={colors.gray[600]} justifyContent="space-between">
          <Typography variant="base-muted">Service Fee</Typography>
          {loading ? (
            <Skeleton w="10rem" h="1rem" />
          ) : (
            <Typography variant="base-muted">
              {typeof serviceFee === 'number'
                ? percentageToPercentageLabel(serviceFee * 100, 5, true)
                : '-'}
            </Typography>
          )}
        </HStack>
      </Flex>
    </HStack>
  );
};
